import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { DashboardShell } from "@/components/DashboardShell";
import { CUSTOMER_NAV, PARTNER_NAV } from "@/lib/nav";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  TICKET_CATEGORIES,
  TICKET_CATEGORY_LABEL,
  TICKET_STATUSES,
  TICKET_STATUS_LABEL,
  createTicket,
  listMyTickets,
  type TicketCategory,
  type TicketStatus,
} from "@/lib/support";
import { CategoryPill, StatusPill } from "@/components/support/TicketPieces";

type Tab = "all" | TicketStatus;

export const Route = createFileRoute("/_authenticated/support/")({
  head: () => ({
    meta: [
      { title: "Support Tickets · Landoo" },
      {
        name: "description",
        content:
          "Raise a Landoo support ticket for account, listing, verification, subscription or technical help and track its progress.",
      },
      { property: "og:title", content: "Support Tickets · Landoo" },
      {
        property: "og:description",
        content: "Raise a Landoo support ticket and follow every reply from our support team.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: SupportListPage,
});

function SupportListPage() {
  const { user, profile, roles } = useAuth();
  const isPartner = roles.includes("partner");
  const qc = useQueryClient();

  const [tab, setTab] = useState<Tab>("all");
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<TicketCategory>("account");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["support", "mine", user?.id],
    queryFn: () => listMyTickets(user!.id),
    enabled: !!user,
  });

  const list = useMemo(
    () => (tab === "all" ? rows : rows.filter((r) => r.status === tab)),
    [rows, tab],
  );

  const create = useMutation({
    mutationFn: () =>
      createTicket({
        userId: user!.id,
        userName: profile?.full_name ?? null,
        userEmail: profile?.email ?? user?.email ?? null,
        userRole: isPartner ? "partner" : "customer",
        category,
        subject,
        description,
        file,
      }),
    onSuccess: () => {
      toast.success("Ticket submitted. Our team will reply shortly.");
      setOpen(false);
      setSubject("");
      setDescription("");
      setFile(null);
      setCategory("account");
      qc.invalidateQueries({ queryKey: ["support", "mine"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Could not create ticket"),
  });

  const submit = () => {
    if (subject.trim().length < 4) return toast.error("Please add a clearer subject");
    if (description.trim().length < 10)
      return toast.error("Please describe the issue in more detail");
    create.mutate();
  };

  return (
    <DashboardShell
      title="Support"
      role={isPartner ? "Partner" : "Customer"}
      items={isPartner ? PARTNER_NAV : CUSTOMER_NAV}
    >
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {(["all", ...TICKET_STATUSES] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                tab === t ? "bg-brown-dark text-white" : "bg-secondary text-brown-dark"
              }`}
            >
              {t === "all" ? "All" : TICKET_STATUS_LABEL[t]}
            </button>
          ))}
        </div>
        <Button size="sm" className="ml-auto" onClick={() => setOpen((v) => !v)}>
          {open ? "Cancel" : "New Ticket"}
        </Button>
      </div>

      {open && (
        <div className="mt-4 rounded-xl border border-border bg-card p-4">
          <h2 className="text-sm font-semibold text-brown-dark">Create a support ticket</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as TicketCategory)}
                className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
              >
                {TICKET_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {TICKET_CATEGORY_LABEL[c]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Subject</label>
              <Input
                value={subject}
                maxLength={140}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Short summary of the issue"
                className="mt-1"
              />
            </div>
          </div>
          <div className="mt-3">
            <label className="text-xs font-medium text-muted-foreground">Description</label>
            <textarea
              value={description}
              maxLength={4000}
              rows={5}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Tell us what happened, with any listing or invoice references."
              className="mt-1 w-full resize-none rounded-lg border border-border bg-background p-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-secondary px-3 py-1.5 text-xs font-medium text-brown-dark">
              {file ? file.name.slice(0, 28) : "Attachment (optional)"}
              <input
                type="file"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </label>
            {file && (
              <button
                type="button"
                onClick={() => setFile(null)}
                className="text-xs text-muted-foreground underline"
              >
                Remove
              </button>
            )}
            <Button size="sm" className="ml-auto" onClick={submit} disabled={create.isPending}>
              {create.isPending ? "Submitting…" : "Submit Ticket"}
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="mt-6 text-sm text-muted-foreground">Loading your tickets…</p>
      ) : list.length === 0 ? (
        <p className="mt-6 rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No tickets in this view. Use “New Ticket” to reach our support team.
        </p>
      ) : (
        <ul className="mt-4 space-y-3">
          {list.map((t) => (
            <li key={t.id} className="rounded-xl border border-border bg-card p-4">
              <div className="flex flex-wrap items-center gap-2">
                <StatusPill status={t.status} />
                <CategoryPill category={t.category} />
                <span className="text-[11px] font-semibold text-muted-foreground">
                  {t.ticket_no}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {new Date(t.created_at).toLocaleString()}
                </span>
              </div>
              <p className="mt-1 text-sm font-semibold text-brown-dark">{t.subject}</p>
              <p className="line-clamp-2 text-xs text-muted-foreground">{t.description}</p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <Link
                  to="/support/$ticketId"
                  params={{ ticketId: t.id }}
                  className="text-xs font-semibold text-brown-dark underline"
                >
                  View &amp; reply
                </Link>
                {t.last_reply_at && (
                  <span className="text-[11px] text-muted-foreground">
                    Last reply {new Date(t.last_reply_at).toLocaleString()}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </DashboardShell>
  );
}
